/* eslint-disable react-hooks/exhaustive-deps */
import { LicenseInfo } from '@mui/x-data-grid-pro';
import AffairApiService from 'api/AffairApiService';
import AuthApiService from 'api/AuthApiService';
import CustomersApiService from 'api/CustomersApiService';
import ResourceAPI from 'api/ResourceAPI';
import UserApiService from 'api/UserApiService';
import mfaApiService from 'api/MfaApiService';
import findActiveCustomer from 'helpers/findActiveCustomer';
import useAppRights from 'hooks/rights/useAppRights';
import useApi from 'hooks/useApi';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { adminRoutes, bmRoutes, clientRoutes, consultantRoutes, teamLeaderRoutes } from 'routes';
import AppRouter from 'routes/AppRouter';
import Token from 'types/models/Token';
import { RouteType } from 'types/RouteType';
import './App.scss';
import useAutoLogoutOnInactivity from './hooks/useAutoLogoutOnInactivity';
import useUserRoles from './hooks/useUserRoles';
import './i18n';
import {
  loadAffairsAction,
  loadCustomerAction,
  loadCustomersAction,
  loadDevisesAction,
  loadRolesAction,
  loadTokenAction,
  loadUserAction,
  updateTermsOfUse,
} from './store/actions/appActions';
import { setSnackbarAction } from './store/actions/snackbarActions';
import { useSelector } from './store/hooks';
import { useAppState } from './store/Provider';
import Loader from 'components/Loader';
import { getDecodedIdToken, getSubdomain } from 'helpers/utils';
import getCompanyByHeader from 'helpers/getCompanyByHeader';

const App: React.FC = () => {
  const { dispatch } = useAppState();
  const appState = useSelector((state) => state.app);
  const snackbar = useSelector((state) => state.snackbar);
  const [isInitialDataReady, setIsInitialDataReady] = useState(false);
  const userRoles = useUserRoles();
  const { canFetchAllDeviseForCatalog } = useAppRights();
  const [appIsReady, setAppIsReady] = useState<boolean>(false);
  // const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState<boolean>(false);
  const [isFetchingRoles, setIsFetchingRoles] = useState<boolean>(false);
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(false);
  const [isFetchingCustomer, setIsFetchingCustomer] = useState<boolean>(false);
  const [isRouterReady, setIsRouterReady] = useState<boolean>(false);
  const [authUri, setAuthUri] = useState<string>('');
  const [mfaChecked, setMfaChecked] = useState<boolean>(false);
  const muiProVersion = process.env.REACT_APP_MUI_PRO ?? '';
  LicenseInfo.setLicenseKey(muiProVersion);

  const { makeCall } = useApi();

  const handleLogout = useCallback(async () => {
    dispatch(loadTokenAction());

    try {
      const logoutUri = await AuthApiService.logout();
      sessionStorage.removeItem('mfa_check_done');
      sessionStorage.removeItem('mfa_check_result');
      localStorage.removeItem('cognito-token');
      window.location.href = logoutUri;
    } catch (error) {
      console.error('Logout failed:', error);
      const fallbackUri = await AuthApiService.getAuthAuthorization();
      window.location.href = fallbackUri;
    }
  }, [dispatch]);

  useAutoLogoutOnInactivity(Boolean(appState.token?.id_token), handleLogout);

  useEffect(() => {
    // Freshworks widget settings (avoid inline script injection so CSP can drop 'unsafe-inline')
    (window as any).fwSettings = {
      widget_id: 203000000076,
      hideLauncher: true,
    };

    if (typeof (window as any).FreshworksWidget !== 'function') {
      const n: any = (...args: any[]) => n.q.push(args);
      n.q = [];
      (window as any).FreshworksWidget = n;
    }
    (window as any).FreshworksWidget('hide', 'launcher');

    // Create the script element for the widget itself
    const script2 = document.createElement('script');
    script2.type = 'text/javascript';
    script2.src = 'https://euc-widget.freshworks.com/widgets/203000000076.js';
    script2.async = true;
    script2.defer = true;

    document.body.appendChild(script2);

    // Cleanup function to remove scripts when component unmounts
    return () => {
      if (document.body.contains(script2)) document.body.removeChild(script2);
    };
  }, []);

  useEffect(() => {
    if (
      appState.token?.id_token &&
      appState.token.id_token !== '' &&
      appState.token?.id_token !== undefined &&
      !appIsReady
    ) {
      setAppIsReady(true);
    }
  }, [appIsReady, appState.token?.id_token]);

  useEffect(() => {
    if (appState.customer?.slug) {
      setActiveSlug(appState.customer?.slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.customer?.id]);

  useEffect(() => {
    const checkSubdomain = async () => {
      try {
        const currentHostname = window.location.hostname;
        const currentSub = getSubdomain(currentHostname);
        const company = getCompanyByHeader(currentSub);
        document.title = company.title;
        const stored = localStorage.getItem('cognito-token');
        if (!stored) return;

        const cognitoToken = JSON.parse(stored) as Token;
        const decodedIdToken = getDecodedIdToken(cognitoToken);

        if (!company?.cognito?.clientId) return;

        if (company.cognito.clientId !== decodedIdToken.aud) {
          console.warn('Subdomain mismatch — logging out');

          const getUri = async () => {
            const uri = await AuthApiService.getAuthAuthorizationLogout();
            localStorage.removeItem('cognito-token');
            // Clear MFA session storage on logout
            sessionStorage.removeItem('mfa_check_done');
            sessionStorage.removeItem('mfa_check_result');
            window.location.href = uri;
          };
          await getUri();
        }
      } catch (err) {
        console.error('Subdomain check failed:', err);
        localStorage.removeItem('cognito-token');
      }
    };

    checkSubdomain();
  }, [dispatch]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (appIsReady) {
      return;
    }
    // Run this after the sign-in

    let cognitoToken: string | null | Token = localStorage.getItem('cognito-token');
    if (cognitoToken) {
      cognitoToken = JSON.parse(cognitoToken) as Token;
    }

    const refreshCognitoToken = async (cognitoToken: Token) => {
      try {
        setIsRefreshingToken(true);

        const result = await AuthApiService.getRefreshTokenUri(
          cognitoToken.refresh_token as string
        );

        if (result.error || !result.tokenUri) {
          await handleLogout();
          return;
        }

        const { tokenUri } = result;
        const tokenResponse =
          typeof tokenUri === "string"
            ? await AuthApiService.getAuthToken(tokenUri)
            : tokenUri;

        if (!tokenResponse?.access_token) {
          await handleLogout();
          return;
        }

        localStorage.setItem(
          'cognito-token',
          JSON.stringify({
            ...tokenResponse,
            refresh_token: cognitoToken.refresh_token,
          })
        );
        setAppIsReady(true);

      } catch (err) {
        console.log("🔴 unexpected:", err);
        await handleLogout();
      } finally {
        setIsRefreshingToken(false);
      }
    };

    if (
      cognitoToken &&
      Object.keys(cognitoToken).length &&
      isTokenStillValid(Number((cognitoToken as Token)?.expires_at))
    ) {
      // Rehydrate auth from localStorage after full-page redirects like MFA enforcement.
      if (!appState.token?.id_token) {
        dispatch(loadTokenAction(cognitoToken as Token));
      }
      setAppIsReady(true);
      return;
    }
    if (cognitoToken && Object.keys(cognitoToken).length) {
      refreshCognitoToken(cognitoToken as Token);
      return;
    }

    if (!authUri && !appState.token?.code) {
      const getUri = async () => {
        const uri = await AuthApiService.getAuthAuthorization();
        setAuthUri(uri);
      };
      getUri();
    }
    if (authUri && !appState.token?.code && !appIsReady && !appState.token?.id_token) {
      window.location.href = authUri;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AuthApiService, authUri, appState.token, appIsReady]);

  const isTokenStillValid = (expires_at: number) => expires_at - Date.now() > 0;
  // Fetch customers.

  useEffect(() => {
    if (appIsReady && isRouterReady) {
      // eslint-disable-next-line consistent-return
      const fetchCustomers = async () => {
        try {
          setIsFetchingCustomer(true);
          const customersFetched = await makeCall(CustomersApiService.fetchAll({ size: -1 }));
          dispatch(loadCustomersAction(customersFetched));
          dispatch(loadCustomerAction(findActiveCustomer(customersFetched)));
        } catch (e) {
          const result = (e as Error).message;

          if (result) {
            dispatch(
              setSnackbarAction({
                message: `Fetch customers request failed : ${result}`,
                open: true,
                severity: 'error',
              })
            );
          } else {
            dispatch(setSnackbarAction({ message: `Fetch customers request failed :`, open: true, severity: 'error' }));
          }
        } finally {
          setIsFetchingCustomer(false);
        }
      };
      fetchCustomers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appIsReady, isRouterReady]);

  // Fetch affairs from active customer.
  useEffect(() => {
    const fetchAffairsByCustomer = async () => {
      try {
        if (appState.customer) {
          const affairsRetrieved = await makeCall(AffairApiService.fetchByCustomerId(appState.customer.id));
          dispatch(loadAffairsAction(affairsRetrieved));
          setIsInitialDataReady(false);
        }
      } catch (e) {
        const result = (e as Error).message;
        if (result) {
          dispatch(
            setSnackbarAction({
              message: `Fetch affairs failed : ${result}`,
              open: true,
              severity: 'error',
            })
          );
        } else {
          dispatch(
            setSnackbarAction({
              message: `Fetch affairs failed : ${result}`,
              open: true,
              severity: 'error',
            })
          );
        }
      }
    };
    const fetchAllDeviseForCatalog = async () => {
      try {
        const {
          data: { datas },
        } = await ResourceAPI.fetchAll('devises');
        dispatch(loadDevisesAction(datas));
      } catch (error) {
        const result = (error as Error).message;
        if (result) {
          dispatch(
            setSnackbarAction({
              message: `Fetch complexities from active catalog failed  : ${result}`,
              open: true,
              severity: 'error',
            })
          );
        } else
          dispatch(
            setSnackbarAction({
              message: 'Fetch complexities from active catalog failed ',
              open: true,
              severity: 'error',
            })
          );
      }
    };
    if (appState.customer?.id && canFetchAllDeviseForCatalog) {
      fetchAllDeviseForCatalog();
      fetchAffairsByCustomer();
    }
  }, [appState.customer, canFetchAllDeviseForCatalog, isInitialDataReady]);

  useEffect(() => {
    if (appIsReady && isRouterReady) {
      const getActiveUser = async () => {
        try {
          setIsFetchingUser(true);
          const data = await makeCall(UserApiService.fetchMe(), 'Fetch your informations failed');
          if (data) {
            dispatch(updateTermsOfUse(data.acceptedTermsOfuse));
            dispatch(loadUserAction(data.me));
          }
        } finally {
          setIsFetchingUser(false);
        }
      };
      getActiveUser();
    }
  }, [appIsReady, isRouterReady]);
  useEffect(() => {
    const getActiveUserRole = async () => {
      try {
        if (appState.user) {
          setIsFetchingRoles(true);
          const roles = !appState.user.is_client ? await UserApiService.fetchUserRole() : ['customer' as const];
          dispatch(loadRolesAction(roles));
          setIsInitialDataReady(true);
        }
      } catch (error: any) {
        dispatch(
          setSnackbarAction({ message: `Fetch your roles failed : ${error.message}`, open: true, severity: 'error' })
        );
      } finally {
        setIsFetchingRoles(false);
      }
    };
    if (appState.customer?.id && appIsReady && appState.user?.id && appState.acceptedTermsOfuse === true)
      getActiveUserRole();
  }, [appState.customer?.id, appIsReady, appState.user, appState.acceptedTermsOfuse]);

  const [activeSlug, setActiveSlug] = useState<string>('');

  const [availableRoutes, setAvailablesRoutes] = useState<RouteType[]>();
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const getRoutes = () => {
      if (userRoles.isConsultant) return setAvailablesRoutes(consultantRoutes);
      if (userRoles.isClient) return setAvailablesRoutes(clientRoutes);
      if (userRoles.isAdmin) return setAvailablesRoutes(adminRoutes);
      if (userRoles.isDeliveryCoordinator) return setAvailablesRoutes(teamLeaderRoutes);
      if (userRoles.isManager) {
        return setAvailablesRoutes(bmRoutes);
      }
      return setAvailablesRoutes(consultantRoutes);
    };
    const delayDebounceFn = setTimeout(() => getRoutes(), 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [userRoles, appState.customers]);

  const progress = useRef(0);

  React.useEffect(() => {
    progress.current = 0;

    const timer = setInterval(
      () => {
        progress.current = progress.current >= 100 ? 100 : progress.current + 2;
      },
      snackbar?.duration ? snackbar?.duration / 100 : 50
    );
    return () => {
      clearInterval(timer);
      progress.current = 0;
    };
  }, [snackbar?.duration !== 0]);

  const locationForRedirect =
    (window.location.pathname.includes('dashboard') ||
      window.location.pathname.includes('management') ||
      window.location.pathname.includes('order') ||
      window.location.pathname.includes('catalogs') ||
      window.location.pathname.includes('clients') ||
      window.location.pathname.includes('administration') ||
      window.location.pathname.includes('deliverables') ||
      window.location.pathname.includes('changebaseline')) &&
    window.location.pathname.includes(activeSlug);

  const isAnyOperationLoading = isRefreshingToken || isFetchingRoles || isFetchingUser || isFetchingCustomer;

  // Set router ready after initial render
  useEffect(() => {
    setIsRouterReady(true);
  }, []);

  // Check MFA status after customer and user are loaded
  useEffect(() => {
    const checkMfaStatus = async () => {
      // Don't check if already on MFA settings page
      const isOnMfaPage = window.location.pathname.includes('/settings/mfa');

      // Check if we already did MFA check in this session
      const mfaCheckResult = sessionStorage.getItem('mfa_check_result');
      const mfaCheckDone = sessionStorage.getItem('mfa_check_done');

      console.log('MFA Check - App.tsx:', {
        appIsReady,
        customerSlug: appState.customer?.slug,
        userId: appState.user?.id,
        mfaChecked,
        isOnMfaPage,
        mfaCheckResult,
        mfaCheckDone,
      });

      if (
        appIsReady &&
        appState.customer?.slug &&
        appState.user?.id &&
        !mfaChecked &&
        !isOnMfaPage &&
        !mfaCheckDone &&
        !mfaCheckResult
      ) {
        try {
          // Check BOTH database (appState.user) and Cognito (API)
          // Database is the source of truth for MFA requirement (set by admin)
          // Cognito is the source of truth for MFA setup status (set by user)
          const mfaStatus = await mfaApiService.getMfaStatus();
          const userMfaEnabled = appState.user.mfa_enabled || false;
          const userMfaSetupCompleted = appState.user.mfa_setup_completed || false;

          console.log('MFA Status Check:', {
            fromDatabase: {
              mfaEnabled: userMfaEnabled,
              mfaSetupCompleted: userMfaSetupCompleted,
            },
            fromCognito: mfaStatus,
          });

          // Determine if MFA setup is required:
          // 1. Admin enabled MFA in database (userMfaEnabled = true)
          // 2. User hasn't completed setup yet
          const mfaRequired =
            (userMfaEnabled || mfaStatus.mfaEnabled) && !userMfaSetupCompleted && !mfaStatus.mfaSetupCompleted;

          if (mfaRequired) {
            console.log('MFA required - redirecting to setup');
            sessionStorage.setItem('mfa_check_done', 'redirecting');
            sessionStorage.setItem('mfa_check_result', 'required');
            window.location.href = `/${appState.customer.slug}/settings/mfa`;
          } else {
            console.log('MFA not required or already completed');
            sessionStorage.setItem('mfa_check_done', 'true');
            sessionStorage.setItem('mfa_check_result', 'passed');
            setMfaChecked(true);
          }
        } catch (error) {
          console.error('Error checking MFA status:', error);
          // On error, fall back to checking just the user object
          if (appState.user.mfa_enabled && !appState.user.mfa_setup_completed) {
            console.log('API error but database shows MFA required - redirecting');
            window.location.href = `/${appState.customer.slug}/settings/mfa`;
          } else {
            sessionStorage.setItem('mfa_check_done', 'error');
            sessionStorage.setItem('mfa_check_result', 'error');
            setMfaChecked(true);
          }
        }
      } else if (isOnMfaPage) {
        // Mark as checked if already on MFA page to prevent redirect loop
        sessionStorage.setItem('mfa_check_done', 'on_mfa_page');
        sessionStorage.setItem('mfa_check_result', 'on_mfa_page');
        setMfaChecked(true);
      } else if (mfaCheckDone || mfaCheckResult) {
        // Already checked in this session
        console.log('MFA check already done in this session');
        setMfaChecked(true);
      }
    };

    checkMfaStatus();
  }, [appIsReady, appState.customer?.slug, appState.user?.id, mfaChecked]);

  if (isAnyOperationLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
      </div>
    );
  }

  return (
    <Router>
      <AppRouter
        appIsReady={appIsReady}
        roles={appState.roles}
        availableRoutes={availableRoutes}
        activeSlug={activeSlug}
        progress={progress}
        locationForRedirect={locationForRedirect}
      />
    </Router>
  );
};

export default App;
